import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { cloneDeep } from 'lodash-es';
import { CkNormeValues } from '../common/models/amm-controllo.type';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { MyAllElemCollegioSindacaleEntity, MyAllElemRevisoriControlloContabileEntity, MyVerbale } from '../../../models/interfaces/revisione.interface';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { withCkNormeGuard } from '../common/operators/with-ck-norme-guard.operator';
import { controlloCheckOrgControllo, controlloSocioOrganoControlloRevisoreLegale } from '../common/helpers/norme-store-update.helper';

const AMM_CTRL_PATH = 'my_SezioneI__Rilevazione.my_AmministrazioneControllo';

export const my_AllElemCollegioSindacaleListenerHelper = (form: FormGroup, store: Store<Revisione53State>, valoreNormaAttesa: CkNormeValues) => {
    const amministrazioneControlloGroup = getGroup(form, AMM_CTRL_PATH);
    const componentiCollegioSindacaleControl = getControl(amministrazioneControlloGroup, 'my_AllCollegioSindacale.my_AllElemCollegioSindacale');
    const revisoriControlloContabileControl = getControl(amministrazioneControlloGroup, 'my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile');

    return componentiCollegioSindacaleControl.valueChanges
        .pipe(withChangeDebounced(componentiCollegioSindacaleControl), withCkNormeGuard(form, valoreNormaAttesa))
        .subscribe((componentiCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = []) => {
            const revisoriControlloContabile = revisoriControlloContabileControl.value ?? [];
            controlloCheckOrgControllo(form, store, componentiCollegioSindacale);
            controlloSocioOrganoControlloRevisoreLegale(form, store, componentiCollegioSindacale, revisoriControlloContabile);
        });
};

export const my_AllCollSindCkRevisoreListenerHelper = (form: FormGroup, store: Store<Revisione53State>, isForSrl: boolean, valoreNormaAttesa: CkNormeValues) => {
    const amministrazioneControlloGroup = getGroup(form, AMM_CTRL_PATH);
    const ckSindaciRevisoriPath: string = isForSrl ? 'my_NormeSrl.my_CkSrlSindaciRevisori' : 'my_NormeSpaOrdinario.my_CkSpoSindaciRevisori';
    const ckSindaciRevisoriControl = getControl(amministrazioneControlloGroup, ckSindaciRevisoriPath);
    const componentiCollegioSindacaleControl = getControl(amministrazioneControlloGroup, 'my_AllCollegioSindacale.my_AllElemCollegioSindacale');

    return componentiCollegioSindacaleControl.valueChanges
        .pipe(withChangeDebounced(componentiCollegioSindacaleControl), withCkNormeGuard(form, valoreNormaAttesa))
        .subscribe((componentiCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = []) => {
            const tuttiRevisori = componentiCollegioSindacale.every((c) => c.my_AllCollSindCkRevisore === '1');
            const valoreCkSindaciRevisori = tuttiRevisori ? '1' : '';

            ckSindaciRevisoriControl.patchValue(valoreCkSindaciRevisori, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                if (isForSrl) {
                    myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlSindaciRevisori = valoreCkSindaciRevisori;
                } else {
                    myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaOrdinario.my_CkSpoSindaciRevisori = valoreCkSindaciRevisori;
                }
                return myVerbaleClone;
            });
        });
};

export const my_AllCollSindCkSocioListenerHelper = (form: FormGroup, store: Store<Revisione53State>, valoreNormaAttesa: CkNormeValues) => {
    const ctrl = getControl(form, `${AMM_CTRL_PATH}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`);
    ctrl.valueChanges.pipe(withChangeDebounced(ctrl), withCkNormeGuard(form, valoreNormaAttesa)).subscribe((allElem: MyAllElemCollegioSindacaleEntity[]) => {
        controlloCheckOrgControllo(form, store, allElem);
    });
};

export const my_AllCollSindCaricaListenerHelper = (form: FormGroup, store: Store<Revisione53State>, valoreNormaAttesa: CkNormeValues) => {
    const componentiCollegioSindacaleArray = getFormArray(form, `${AMM_CTRL_PATH}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`);

    return componentiCollegioSindacaleArray.valueChanges
        .pipe(withChangeDebounced(componentiCollegioSindacaleArray), withCkNormeGuard(form, valoreNormaAttesa))
        .subscribe((componentiCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = []) => {
            componentiCollegioSindacale.forEach((componenteCollegioSindacale, index) => {
                const codiceCarica = componenteCollegioSindacale.my_AllCollSindCarica;

                store
                    .select(Revisione53DatiBusinessSelectors.getAllCollSindCaricaSpec(codiceCarica))
                    .pipe(take(1))
                    .subscribe((carica) => {
                        const descrizioneCarica = carica?.descrizione ?? '';
                        const componenteCollegioSindacaleGroup = componentiCollegioSindacaleArray.at(index) as FormGroup;
                        const descrizioneCaricaControl = getControl(componenteCollegioSindacaleGroup, 'my_AllCollSindCaricaDesc');

                        descrizioneCaricaControl.patchValue(descrizioneCarica, { emitEvent: false });

                        updateVerbaleOnStore(store, (myVerbale) => {
                            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                            myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllCollegioSindacale.my_AllElemCollegioSindacale =
                                componentiCollegioSindacaleArray.value;
                            return myVerbaleClone;
                        });
                    });
            });
        });
};

export const my_AllRevisoriControlloContabileListenerHelper = (form: FormGroup, store: Store<Revisione53State>, valoreNormaAttesa: CkNormeValues) => {
    const amministrazioneControlloGroup = getGroup(form, AMM_CTRL_PATH);
    const revisoriControlloContabileControl = getControl(amministrazioneControlloGroup, 'my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile');
    const collegioSindacaleControl = getControl(amministrazioneControlloGroup, 'my_AllCollegioSindacale.my_AllElemCollegioSindacale');

    return revisoriControlloContabileControl.valueChanges
        .pipe(withChangeDebounced(revisoriControlloContabileControl), withCkNormeGuard(form, valoreNormaAttesa))
        .subscribe((revisoriControlloContabile: MyAllElemRevisoriControlloContabileEntity[]) => {
            const componentiCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = collegioSindacaleControl.value ?? [];
            controlloSocioOrganoControlloRevisoreLegale(form, store, componentiCollegioSindacale, revisoriControlloContabile);
        });
};

export const my_AllRevisoriControlloContabileOrganoListenerHelper = (form: FormGroup, store: Store<Revisione53State>, valoreNormaAttesa: CkNormeValues) => {
    const amministrazioneControlloGroup = getGroup(form, AMM_CTRL_PATH);
    const ctrl = getControl(amministrazioneControlloGroup, 'my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile');
    ctrl.valueChanges.pipe(withChangeDebounced(ctrl), withCkNormeGuard(form, valoreNormaAttesa)).subscribe((allElem: MyAllElemRevisoriControlloContabileEntity[]) => {
        const elemCollegioSindacale = getControl(amministrazioneControlloGroup, 'my_AllCollegioSindacale.my_AllElemCollegioSindacale').value;
        controlloSocioOrganoControlloRevisoreLegale(form, store, elemCollegioSindacale, allElem);
    });
};
